import { type ComponentConfig } from "@puckeditor/core";
import { Reviews, type ReviewsProps } from "./reviews";
import { ReviewsFields } from "./reviews.fields";
import { RegenerableTextField } from "../editor/RegenerableTextField";
import { getWindowJobsiteConfig } from "../../types/runtime-config";

export const ReviewsConfig: ComponentConfig<ReviewsProps> = {
  label: "Reviews",
  fields: {
    heading: {
      ...ReviewsFields.heading,
      contentEditable: true,
      render: ({
        value,
        onChange,
        field,
      }: {
        value: any;
        onChange: (val: any) => void;
        field: any;
      }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="home.reviews.heading"
          label="Heading"
          multiline={false}
          fieldMeta={field?.metadata}
        />
      ),
    },
    subheading: {
      ...ReviewsFields.subheading,
      contentEditable: true,
      render: ({
        value,
        onChange,
        field,
      }: {
        value: any;
        onChange: (val: any) => void;
        field: any;
      }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="home.reviews.subheading"
          label="Subheading"
          multiline={true}
          fieldMeta={field?.metadata}
        />
      ),
    },
    layout: ReviewsFields.layout,
    showRatings: ReviewsFields.showRatings,
    showDates: ReviewsFields.showDates,
    useRealReviews: ReviewsFields.useRealReviews,
    reviewLimit: ReviewsFields.reviewLimit,
    minRating: ReviewsFields.minRating,
    reviews: ReviewsFields.reviews,
    backgroundColor: ReviewsFields.backgroundColor,
  },
  resolveFields: (data, { fields }) => {
    // Hide manual reviews array when using real reviews from the database.
    // Absent means "yes" — generator-authored blocks omit the prop and the
    // component defaults to fetching real reviews.
    if ((data.props as any).useRealReviews !== "no") {
      const { reviews: _reviews, ...rest } = fields as any;
      return rest;
    }

    // On the manual list, the database-query controls have nothing to query.
    const visible = { ...(fields as any) };
    delete visible.reviewLimit;
    delete visible.minRating;

    // And a per-review rating is not read when ratings are hidden.
    if ((data.props as any).showRatings === "no" && visible.reviews?.arrayFields?.rating) {
      const { rating: _rating, ...arrayFields } = visible.reviews.arrayFields;
      visible.reviews = { ...visible.reviews, arrayFields };
    }

    return visible;
  },
  defaultProps: {
    heading: "What Our Customers Say",
    subheading: "See what our satisfied customers have to say about working with us",
    layout: "grid",
    showRatings: "yes",
    showDates: "yes",
    useRealReviews: "yes",
    reviewLimit: 6,
    minRating: 4,
    reviews: [
      {
        name: "Sarah Johnson",
        location: "Phoenix, AZ",
        rating: 5,
        text: "Exceptional service! They arrived on time, diagnosed the problem quickly, and had it fixed within an hour. Professional and courteous.",
        date: "2024-01-15",
      },
      {
        name: "Michael Chen",
        location: "Phoenix, AZ",
        rating: 5,
        text: "Best decision we made was signing up for their maintenance plan. No more surprise breakdowns and energy bills have decreased significantly. Fair pricing and excellent work.",
        date: "2024-01-10",
      },
      {
        name: "Jennifer Martinez",
        location: "Phoenix, AZ",
        rating: 5,
        text: "Called them for an emergency repair on a Sunday. They came within 2 hours and fixed our system fast and fixed our system. Fair pricing and excellent work.",
        date: "2024-01-05",
      },
    ],
  },
  render: ({ id, puck, editMode, ...props }) => {
    // Access business data from Puck metadata OR window config (fallback)
    const puckMetadata = puck?.metadata as any;
    const windowConfig = getWindowJobsiteConfig();
    const business = puckMetadata?.business || windowConfig?.business;

    // Pass siteId and apiUrl directly from metadata — avoids iframe timing issues
    // where window.__SERVICEPRO_CONFIG__ isn't set yet on first render
    const puckSiteId = puckMetadata?.site?.id as string | undefined;
    const puckApiUrl = puckMetadata?.apiUrl as string | undefined;

    const resolvedProps = {
      ...props,
      subheading:
        props.subheading ||
        (business?.city
          ? `See what your neighbors in ${business.city} are saying about us.`
          : "See what our customers are saying about us."),
    };

    // The business profile's Google count paints the badge first; the API's total,
    // which adds every other platform, replaces it once reviews load.
    const totalReviewCount: number | undefined =
      typeof business?.googleReviewCount === "number" ? business.googleReviewCount : undefined;

    return (
      <Reviews
        id={id}
        {...resolvedProps}
        puckSiteId={puckSiteId}
        puckApiUrl={puckApiUrl}
        totalReviewCount={totalReviewCount}
        // The Simple UI canvas renders through plain <Render>, so editor-ness
        // comes from the window config there, as EmergencyBand resolves it.
        isEditing={Boolean(windowConfig?.isEditing ?? editMode)}
      />
    );
  },
};
